import React, { FC, useEffect, useRef, useState } from 'react';
import MainTemplate from '../components/MainTemplate';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { PageProps, Pages } from '../App';
import ReactGA from 'react-ga4';
import { GAEvents } from '../gtag/constants';
import { Snackbar, Tooltip, useTheme } from '@mui/material';

const RESEND_COOLDOWN_SECONDS = 10;

const Started: FC<PageProps> = ({
  setPage,
  userData,
  startExperience,
  accountId
}) => {
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const intervalRef = useRef<number | undefined>();

  const restartHandler = async () => {
    setPage(Pages.Landing);
    ReactGA.event(GAEvents.StartOverStartPage);
  };

  const submitHandler = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    startCountdown();

    ReactGA.event(GAEvents.Resend);

    setIsLoading(true);
    const res2 = await startExperience(true);
    setIsLoading(false);

    if (!res2) return;

    if (res2 && 'status' in res2 && res2.status === 200) {
      setPage(Pages.Started);
    } else {
      setPage(Pages.Error);
      throw 'Could not resend';
    }
  };
  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const startCountdown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);

    intervalRef.current = window.setInterval(() => {
      setResendCooldown((cooldown) => {
        if (cooldown === 1) clearInterval(intervalRef.current);
        return cooldown - 1;
      });
    }, 1000);
  };

  // Clean up interval if the component unmounts before it gets to 0
  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <MainTemplate>
      <Button
        variant="text"
        sx={{
          position: 'absolute',
          right: '10px',
          top: '10px',
          textTransform: 'none'
        }}
        onClick={restartHandler}
      >
        Start over
      </Button>
      <Box
        sx={{
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" sx={{ textAlign: 'center' }}>
          Hello!
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            maxWidth: '250px',
            margin: '0 auto 40px',
            lineHeight: '1.4'
          }}
        >
          A link has been sent to your {userData.phone ? 'phone' : 'email'}. The
          Link will direct you to the app store to download Mobile Fit.
        </Typography>
        <Typography variant="subtitle1" sx={{ marginBottom: 2 }}>
          Haven&apos;t received{' '}
          {userData.phone ? 'the text message' : 'an email'} yet?
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <form onSubmit={submitHandler}>
            <LoadingButton
              type="submit"
              sx={{ marginBottom: 2, background: '#fff' }}
              color="inherit"
              variant="outlined"
              loading={isLoading}
              disabled={resendCooldown > 0}
            >
              Resend {resendCooldown > 0 ? resendCooldown : ''}
            </LoadingButton>
          </form>
          {userData.phone && (
            <Typography variant="subtitle2">SMS charges may apply</Typography>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          margin: '0 0 -40px',
          backgroundColor: '#40445c',
          padding: '16px',
          textAlign: 'center',
          color: '#fff',
          [theme.breakpoints.up('sm')]: {
            margin: '0 -60px -40px'
          }
        }}
      >
        {accountId && (
          <>
            <Typography variant="subtitle1" mb={0.5}>
              Important!{' '}
              <Tooltip title="Click to copy" placement={'top'}>
                <Box
                  display={'inline'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleCopy(accountId)}
                >
                  <svg
                    width="20px"
                    height="20px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ transform: 'translateY(5px)', margin: '0 2px' }}
                  >
                    <path
                      d="M8 4V16C8 17.1046 8.89543 18 10 18L18 18C19.1046 18 20 17.1046 20 16V7.24162C20 6.7034 19.7831 6.18789 19.3982 5.81161L16.0829 2.56999C15.7092 2.2046 15.2074 2 14.6847 2H10C8.89543 2 8 2.89543 8 4Z"
                      stroke="#fff"
                    />
                    <path
                      d="M16 18V20C16 21.1046 15.1046 22 14 22H6C4.89543 22 4 21.1046 4 20V9C4 7.89543 4.89543 7 6 7H8"
                      stroke="#fff"
                    />
                  </svg>
                  <strong>{accountId}</strong>
                </Box>
              </Tooltip>{' '}
              is your account ID.
            </Typography>
            <Typography variant="subtitle2">
              Please use this ID when you log into the Mobile Fit app.
            </Typography>
            <Snackbar
              anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
              open={openSnackbar}
              sx={{ '& .MuiSnackbarContent-root': { minWidth: 100 } }}
              autoHideDuration={2000}
              onClose={() => setOpenSnackbar(false)}
              message="Account ID copied"
            />
          </>
        )}
      </Box>
    </MainTemplate>
  );
};

export default Started;
